import { useFormState } from 'react-hook-form';
import { Button, Divider, IconName, Link, Body, Stack } from '@optimus-web/core';
import { FormInputField, LocalizedText, FormControls } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { TextMessagingConfiguration } from '@mews-commander/data-access';
import { TooltippedIcon } from '@mews-commander/ui';
import { SelectField } from '@mews-commander/form';
import { TextMessagingLabel } from '../components/Typography';
import { useSupportedCountries } from './countries';
import { GeneralFormValues } from './types';
import { UnsupportedCountries } from './UnsupportedCountries';

interface GeneralFormFieldsProps extends Pick<FormControls<GeneralFormValues>, 'submit'> {
    configuration: TextMessagingConfiguration;
    hasSenderNumber: boolean;
}

export const GeneralFormFields = ({ configuration, submit, hasSenderNumber }: GeneralFormFieldsProps) => {
    const { isDirty } = useFormState();
    const { supportedCountries, unsupportedCountryCodes, smsCostPrecision, pricePerCountryDocumentUrl } = configuration;

    const { countryOptions, customSearch } = useSupportedCountries({ supportedCountries, smsCostPrecision });

    return (
        <Stack vertical spacing="400">
            <Stack vertical spacing="200">
                {hasSenderNumber && (
                    <FormInputField
                        label={<LocalizedText textKey={TextKey.SenderPhoneNumberShort} />}
                        name="senderPhoneNumber"
                        size="large"
                        readOnly
                    />
                )}
                <FormInputField
                    captionIconWithTooltip={
                        <TooltippedIcon
                            size="small"
                            value={IconName.Info}
                            tooltip={<LocalizedText textKey={TextKey.EnterpriseShortNameCaption} />}
                        />
                    }
                    helper={<LocalizedText textKey={TextKey.EnterpriseShortNameHint} />}
                    label={
                        <TextMessagingLabel as="span">
                            <LocalizedText textKey={TextKey.ShortName} />
                        </TextMessagingLabel>
                    }
                    name="shortName"
                    placeholder={<LocalizedText textKey={TextKey.EnterpriseShortNamePlaceholder} />}
                    required
                />
                <Divider />
                <SelectField
                    customSearch={customSearch}
                    helper={<LocalizedText textKey={TextKey.TextMessagingConfigurationExcludeCountriesMessage} />}
                    label={
                        <TextMessagingLabel as="span">
                            <LocalizedText textKey={TextKey.ExcludeCountries} />
                        </TextMessagingLabel>
                    }
                    multiselect
                    name="countries"
                    options={countryOptions}
                    placeholder={
                        <LocalizedText textKey={TextKey.TextMessagingConfigurationExcludeCountriesPlaceholder} />
                    }
                    preserveMultiSelectSearch
                />
                <Body variant="secondary">
                    <Link
                        data-test-id="see-price-per-country-list"
                        href={pricePerCountryDocumentUrl}
                        rel="noreferrer"
                        target="_blank"
                    >
                        <LocalizedText textKey={TextKey.TextMessagingSeePricePerCountryList} />
                    </Link>
                </Body>
                <Divider />
                <UnsupportedCountries unsupportedCountryCodes={unsupportedCountryCodes} />
            </Stack>
            <Stack alignment="end">
                <Button disabled={!isDirty} variant="primary" onClick={submit} size="medium" type="submit">
                    <LocalizedText textKey={TextKey.Save} />
                </Button>
            </Stack>
        </Stack>
    );
};
